import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import {
    FileText, UploadSimple, Trash, Eye, ArrowsClockwise,
    CheckCircle, User, UserGear, Warning, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MyResumeCard — candidate self-service CV widget.
 *
 * States:
 *   - loading: skeleton
 *   - no CV: upload dropzone
 *   - has CV: filename + size + uploaded-by-role + [Preview] [Replace] [Delete]
 *
 * Backend contract (V1.5.1):
 *   GET    /api/candidates/{cid}/resume/meta   → {present, size_bytes, ...}
 *   POST   /api/candidates/{cid}/resume        → multipart, replaces current
 *   DELETE /api/candidates/{cid}/resume        → soft-delete
 *   GET    /api/storage/cv/{cid}               → PDF bytes (proxy)
 *
 * Same widget used on:
 *   /candidate/dashboard    (candidate uploads their own CV)
 *   PersonDetailPanel drawer (recruiter uploads on behalf, or views)
 */

const ROLE_LABEL = {
    candidate: { text: "You uploaded", icon: User, cls: "text-cobalt" },
    recruiter: { text: "Uploaded by recruiter", icon: UserGear, cls: "text-[#7E22CE]" },
    admin:     { text: "Uploaded by admin", icon: UserGear, cls: "text-[#F59E0B]" },
    hiring_manager: { text: "Uploaded by hiring manager", icon: UserGear, cls: "text-[#059669]" },
};

function humanBytes(n) {
    if (!n) return "0 KB";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MyResumeCard({ candidateId, readOnly = false, className = "", onChange = null }) {
    const [meta, setMeta] = useState(null);   // {present, size_bytes, ...} | null
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const inputRef = useRef(null);

    // Two modes:
    //   candidateId provided (recruiter using this in PersonDetailPanel) →
    //     use /candidates/{cid}/resume/*  (RBAC-gated)
    //   no candidateId (candidate dashboard) → use /me/candidate/resume/*
    //     (server resolves cid from JWT)
    const isSelf = !candidateId;
    const basePath = isSelf ? "/me/candidate/resume" : `/candidates/${candidateId}/resume`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(isSelf ? `${basePath}/meta` : `${basePath}/meta`);
            setMeta(data);
        } catch (e) {
            if (e?.response?.status === 403) setMeta({ present: false, forbidden: true });
            else setMeta({ present: false });
        } finally {
            setLoading(false);
        }
    }, [basePath, isSelf]);

    useEffect(() => { load(); }, [load]);

    const handleUpload = async (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) {
            toast.error("File too large — max 4 MB.");
            return;
        }
        const ok = /pdf|word|officedocument/.test(file.type)
                || /\.(pdf|docx?|)$/i.test(file.name);
        if (!ok) {
            toast.error("Please upload a PDF or DOCX file.");
            return;
        }
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(basePath, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("CV uploaded ✓");
            await load();
            // V1.5.8.13 (Feb 17, 2026) — bubble the fresh candidate up so
            // the Kanban row / drawer header chip refreshes without a page
            // reload. Backend now returns {candidate: {...}} in the upload
            // response — pass it through so the parent can setState().
            //
            // Prod bug (V1.5.8.3): uploads succeeded but "NO CV" chip stayed
            // sticky until F5. Root cause: parent was refreshing off a stale
            // candidate row; now we hand it the fresh one directly.
            try { onChange?.({ present: true, candidate: data?.candidate || null }); } catch { /* soft */ }
        } catch (e) {
            const msg = e?.response?.data?.detail || "Upload failed";
            toast.error(msg);
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete your CV from NaukriDaddy? You can upload a new one anytime.")) return;
        setBusy(true);
        try {
            await api.delete(basePath);
            toast.success("CV removed.");
            await load();
            try { onChange?.({ present: false }); } catch { /* soft */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <section data-testid="my-resume-card-loading"
                     className={`border border-[#E5E5E5] bg-white p-5 mb-6 ${className}`}>
                <div className="flex items-center gap-4 animate-pulse">
                    <div className="w-12 h-14 bg-slate-100 rounded" />
                    <div className="flex-1">
                        <div className="h-3 bg-slate-100 w-20 mb-2 rounded" />
                        <div className="h-4 bg-slate-100 w-48 rounded" />
                    </div>
                </div>
            </section>
        );
    }

    // Zero-state — upload prompt
    if (!meta?.present) {
        return (
            <section
                data-testid="my-resume-card-empty"
                className={`border border-dashed border-cobalt/40 bg-gradient-to-br from-cobalt/5 via-white to-white p-5 mb-6 ${className}`}
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-cobalt/10 text-cobalt flex items-center justify-center shrink-0">
                        <FileText size={20} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                            No CV on file
                        </p>
                        <p className="font-heading text-lg font-bold tracking-tight text-ink mb-1">
                            Upload your resume
                        </p>
                        <p className="text-xs text-[#525252] mb-3">
                            PDF or DOCX up to 4 MB. Recruiters can view it when your profile matches their role.
                        </p>
                        {!readOnly && (
                            <>
                                <button
                                    data-testid="my-resume-upload-btn"
                                    onClick={() => inputRef.current?.click()}
                                    disabled={busy}
                                    className="btn-gradient inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider disabled:opacity-60"
                                >
                                    {busy ? <ArrowsClockwise className="animate-spin" size={11} weight="bold" />
                                          : <UploadSimple size={11} weight="bold" />}
                                    {busy ? "Uploading…" : "Upload CV"}
                                </button>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".pdf,.docx,.doc,application/pdf"
                                    className="hidden"
                                    data-testid="my-resume-file-input"
                                    onChange={(e) => handleUpload(e.target.files?.[0])}
                                />
                            </>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    // Has-CV state
    const uploader = ROLE_LABEL[meta.uploaded_by_role] || ROLE_LABEL.candidate;
    const UploaderIcon = uploader.icon;
    // V1.5.8.42 — bug fix: `meta.download_url` already starts with `/api/…`
    // (see backend/routes/candidate_cv.py::get_cv_meta line 260), and
    // `API` is `${REACT_APP_BACKEND_URL}/api`. Concatenating them
    // produced `…/api/api/storage/cv/{cid}` — a double-`/api/` path that
    // matched no route on production → FastAPI's default
    // `{"detail":"Not Found"}` rendered inside the preview iframe (as
    // reported for R Venu / swatimalik90 on Feb 19, 2026). Fix: use the
    // raw backend URL so the paths join to exactly one `/api/`.
    const previewUrl = `${(process.env.REACT_APP_BACKEND_URL || '')}${meta.download_url}`;

    return (
        <>
            <section
                data-testid="my-resume-card-present"
                className={`border border-[#E5E5E5] bg-white p-5 mb-6 ${className}`}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-14 bg-red-50 border border-red-100 flex flex-col items-center justify-center shrink-0 rounded-sm">
                        <FileText size={22} weight="fill" className="text-red-500" />
                        <span className="text-[7px] font-black uppercase text-red-500 mt-0.5">
                            {(meta.content_type || "").includes("pdf") ? "PDF" : "DOC"}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700">
                                <CheckCircle weight="fill" size={10} className="inline mr-1" />
                                On file
                            </p>
                        </div>
                        <p className="text-sm text-ink font-bold mb-1 truncate"
                           data-testid="my-resume-filename">
                            {meta.orig_filename || "resume.pdf"}
                        </p>
                        <p className="text-[11px] text-[#A3A3A3] flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span>{humanBytes(meta.size_bytes)}</span>
                            <span className={`inline-flex items-center gap-1 ${uploader.cls}`}>
                                <UploaderIcon size={10} weight="fill" />
                                {uploader.text}
                            </span>
                            {meta.uploaded_at && (
                                <span>
                                    {new Date(meta.uploaded_at).toLocaleDateString(undefined, {
                                        day: "numeric", month: "short", year: "numeric",
                                    })}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <button
                            data-testid="my-resume-preview-btn"
                            onClick={() => setShowPreview(true)}
                            className="border border-cobalt text-cobalt hover:bg-cobalt hover:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                        >
                            <Eye size={11} weight="bold" /> Preview
                        </button>
                        {!readOnly && (
                            <>
                                <button
                                    data-testid="my-resume-replace-btn"
                                    onClick={() => inputRef.current?.click()}
                                    disabled={busy}
                                    className="border border-[#525252] text-[#525252] hover:bg-[#525252] hover:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                                >
                                    {busy ? <ArrowsClockwise className="animate-spin" size={11} weight="bold" />
                                          : <UploadSimple size={11} weight="bold" />}
                                    Replace
                                </button>
                                <button
                                    data-testid="my-resume-delete-btn"
                                    onClick={handleDelete}
                                    disabled={busy}
                                    title="Remove CV"
                                    className="border border-red-200 text-red-600 hover:bg-red-50 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 transition-colors disabled:opacity-50"
                                >
                                    <Trash size={11} weight="bold" />
                                </button>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".pdf,.docx,.doc,application/pdf"
                                    className="hidden"
                                    data-testid="my-resume-file-input"
                                    onChange={(e) => handleUpload(e.target.files?.[0])}
                                />
                            </>
                        )}
                    </div>
                </div>
            </section>

            {showPreview && (
                <div
                    data-testid="my-resume-preview-modal"
                    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowPreview(false)}
                >
                    <div
                        className="bg-white w-full max-w-4xl h-[85vh] rounded-lg overflow-hidden shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
                            <h3 className="text-sm font-bold text-slate-800">
                                {meta.orig_filename || "Resume"}
                            </h3>
                            <button
                                onClick={() => setShowPreview(false)}
                                data-testid="my-resume-preview-close"
                                className="text-slate-400 hover:text-slate-700 p-1"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </header>
                        <div className="flex-1 bg-slate-100 p-2 md:p-3 overflow-hidden">
                            <iframe
                                src={previewUrl}
                                title="Resume preview"
                                className="w-full h-full bg-white shadow-md rounded-sm"
                                data-testid="my-resume-preview-iframe"
                            />
                        </div>
                        <footer className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-500 shrink-0">
                            Only your team (admin, recruiter, hiring manager) with role-scope access can see this file.
                        </footer>
                    </div>
                </div>
            )}
        </>
    );
}
